import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { SelectedOptions } from '../../types/menu';

export interface RecommendedItemCustomization {
  id: string;
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  selectedOptions: SelectedOptions;
  specialInstructions: string;
  isUpdating?: boolean;
  addedToCart: boolean;
  cartItemId?: string; // Track the cart item ID for updates
}

export interface RecommendedItemsState {
  // Map of parent item ID to its recommended items
  parentRecommendations: {
    [parentItemId: string]: {
      [recommendedItemId: string]: RecommendedItemCustomization;
    };
  };
  // Track which parent item is currently being customized
  currentParentItemId: string | null;
}

const initialState: RecommendedItemsState = {
  parentRecommendations: {},
  currentParentItemId: null,
};

const recommendedItemsSlice = createSlice({
  name: 'recommendedItems',
  initialState,
  reducers: {
    setCurrentParentItem: (state, action: PayloadAction<string>) => {
      state.currentParentItemId = action.payload;
    },
    
    addRecommendedCustomization: (
      state,
      action: PayloadAction<{
        parentItemId: string;
        customization: RecommendedItemCustomization;
      }>
    ) => {
      const { parentItemId, customization } = action.payload;
      
      if (!state.parentRecommendations[parentItemId]) {
        state.parentRecommendations[parentItemId] = {};
      }
      
      state.parentRecommendations[parentItemId][customization.menuItemId] = customization;
    },
    
    updateRecommendedCustomization: (
      state,
      action: PayloadAction<{
        parentItemId: string;
        menuItemId: string;
        updates: Partial<RecommendedItemCustomization>;
      }>
    ) => {
      const { parentItemId, menuItemId, updates } = action.payload;
      
      if (
        state.parentRecommendations[parentItemId] &&
        state.parentRecommendations[parentItemId][menuItemId]
      ) {
        state.parentRecommendations[parentItemId][menuItemId] = {
          ...state.parentRecommendations[parentItemId][menuItemId],
          ...updates,
        };
      }
    },
    
    removeRecommendedCustomization: (
      state,
      action: PayloadAction<{
        parentItemId: string;
        menuItemId: string;
      }>
    ) => {
      const { parentItemId, menuItemId } = action.payload;
      
      if (state.parentRecommendations[parentItemId]) {
        delete state.parentRecommendations[parentItemId][menuItemId];
        
        // If no more recommendations for this parent, remove the parent entry
        if (Object.keys(state.parentRecommendations[parentItemId]).length === 0) {
          delete state.parentRecommendations[parentItemId];
        }
      }
    },
    
    setRecommendedItemUpdating: (
      state,
      action: PayloadAction<{
        parentItemId: string;
        menuItemId: string;
        isUpdating: boolean;
      }>
    ) => {
      const { parentItemId, menuItemId, isUpdating } = action.payload;
      
      if (
        state.parentRecommendations[parentItemId] &&
        state.parentRecommendations[parentItemId][menuItemId]
      ) {
        state.parentRecommendations[parentItemId][menuItemId].isUpdating = isUpdating;
      }
    },
    
    markRecommendedItemAsAddedToCart: (
      state,
      action: PayloadAction<{
        parentItemId: string;
        menuItemId: string;
      }>
    ) => {
      const { parentItemId, menuItemId } = action.payload;
      
      if (
        state.parentRecommendations[parentItemId] &&
        state.parentRecommendations[parentItemId][menuItemId]
      ) {
        state.parentRecommendations[parentItemId][menuItemId].addedToCart = true;
      }
    },
    
    clearRecommendationsForParent: (state, action: PayloadAction<string>) => {
      const parentItemId = action.payload;
      delete state.parentRecommendations[parentItemId];
      
      if (state.currentParentItemId === parentItemId) {
        state.currentParentItemId = null;
      }
    },
    
    clearAllRecommendations: (state) => {
      state.parentRecommendations = {};
      state.currentParentItemId = null;
    },
  },
});

export const {
  setCurrentParentItem,
  addRecommendedCustomization,
  updateRecommendedCustomization,
  removeRecommendedCustomization,
  setRecommendedItemUpdating,
  markRecommendedItemAsAddedToCart,
  clearRecommendationsForParent,
  clearAllRecommendations,
} = recommendedItemsSlice.actions;

export default recommendedItemsSlice.reducer;
